// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE NAME A BORROWER READS FOR A DOCUMENT (Hannah's review, Jesse 2026-11-02 item 4). DISPLAY ONLY — the stored
// file is never renamed (documents.original_name stays as uploaded; staff screens show it).
//
// One helper for every borrower surface: Documents, the guided steps' file lists, Messages attachments, the document
// viewer's title (and the file name the viewer link serves), and the borrower alerts/emails that name a document.
// 🚨 MIRRORED: backend/constants/borrowerDocumentName.js holds the same code; backend/test/borrowerDocumentName.test.js
// runs both on the same cases and fails if they differ.
//
//   "step5-income-1099-2025.pdf"                    → "1099 · 2025"
//   "step5-income-Pay stub 1-2026-10-17.pdf"        → "Pay stub · Oct 17, 2026"
//   "step5-assets-9586-statement-2026-09.pdf"       → "Bank statement ••9586 · Sep 2026"
//   "Bank_Statement_2026_10.pdf"                    → "Bank statement · Oct 2026"
//   "Homeowners Declarations 123 Snapper St (1).pdf" → "Homeowners Declarations 123 Snapper St"
// Anything else: a cleaned name (no extension, no "(1)", no step/section prefix, separators as spaces, first letter
// capitalised) — never "Step5 …".
// ─────────────────────────────────────────────────────────────────────────────

// What Doorly sends, by document type → the name the borrower reads.
export const DOORLY_SENT_NAMES = {
  home_loan_toolkit: 'Your Home Loan Toolkit',
  valuations_notice: 'Your right to a copy of your appraisal',
  credit_score_notice: 'Your credit score disclosure',
  loan_estimate: 'Loan Estimate',
  initial_disclosures: 'Initial disclosures',
  closing_disclosure: 'Closing Disclosure',
  incompleteness_notice: 'Notice about your application',
  adverse_action_notice: 'Notice about your application',
  appraisal_copy: 'Your appraisal',
  smart_estimate: 'Your SmartEstimate',
  title_commitment: 'Title commitment',
  net_sheet: 'Net sheet',
  seller_disclosure: "Seller's disclosure",
  // Doorly made these from what the borrower gave us — "We sent you", never "You sent us" (found by the E2E run, 2026-10-20).
  credit_report: 'Your credit report',
  urla_1003: 'Your loan application',
};
// Doorly's paperwork, signed by the borrower.
export const DOORLY_SIGNED_NAMES = {
  closing_disclosure_signed: 'Closing Disclosure (signed)',
  itp_signed_le: 'Loan Estimate — intent to proceed (signed)',
  borrower_authorization: 'Your authorization (signed)',
  psa_ab_signed: 'Purchase agreement (signed)', borrower_psa_signed: 'Purchase agreement (signed)', ab_psa_signed: 'Purchase agreement (signed)',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// The section words a step's own file names start with (the test document pack: step5-income-…, step5-assets-…).
const SECTION_WORDS = /^(income|incomes|assets?|housing|home|docs?|documents?|uploads?)(\s+|$)/i;
const isYear = (s) => /^(19|20)\d\d$/.test(s);
const isMonth = (s) => /^\d{1,2}$/.test(s) && Number(s) >= 1 && Number(s) <= 12;
const isDay = (s) => /^\d{1,2}$/.test(s) && Number(s) >= 1 && Number(s) <= 31;

/** The trailing date in a list of words → { label, rest } (rest = the words before it), or null. */
function trailingDate(words) {
  const n = words.length;
  const w = (i) => words[n - i];
  if (n >= 3 && isYear(w(3)) && isMonth(w(2)) && isDay(w(1))) {          // 2026 10 17
    return { label: `${MONTHS[Number(w(2)) - 1]} ${Number(w(1))}, ${w(3)}`, rest: words.slice(0, n - 3) };
  }
  if (n >= 3 && isMonth(w(3)) && isDay(w(2)) && isYear(w(1))) {          // 10 17 2026
    return { label: `${MONTHS[Number(w(3)) - 1]} ${Number(w(2))}, ${w(1)}`, rest: words.slice(0, n - 3) };
  }
  if (n >= 2 && isYear(w(2)) && isMonth(w(1)) && /^\d\d$/.test(w(1))) {  // 2026 09
    return { label: `${MONTHS[Number(w(1)) - 1]} ${w(2)}`, rest: words.slice(0, n - 2) };
  }
  if (n >= 1 && isYear(w(1))) return { label: w(1), rest: words.slice(0, n - 1) };   // 2025
  return null;
}

/** The kind of document a file name's words name, for the patterns a borrower's files and our test pack use. */
function titleFor(rest, dated) {
  const t = rest.join(' ').trim();
  const lower = t.toLowerCase();
  if (/^pay ?stubs?( \d{1,2})?$/.test(lower)) return dated ? 'Pay stub' : t.replace(/^pay ?stubs?/i, 'Pay stub');
  if (/^w ?-?2s?$/.test(lower)) return 'W-2';
  if (/^1099s?( (nec|misc|k))?$/.test(lower)) return '1099';
  if (/^(1040|tax return|tax returns|personal tax return)$/.test(lower)) return 'Tax return';
  if (/^(1120 ?s?|1065|business tax return)$/.test(lower)) return 'Business tax return';
  if (/^ytd( earnings| income)?$/.test(lower)) return 'Year-to-date earnings';
  // "9586 statement", "bank 9586 statement", "bank acct1 statement", "statement 9586"
  let m = /^(?:bank )?(?:account )?(\S+) statement$/.exec(lower) || /^(?:bank )?statement (\S+)$/.exec(lower);
  if (m && /\d/.test(m[1]) && !/^(bank|account|mortgage|rent|brokerage|investment|retirement|donor|sale)$/.test(m[1])) {
    const digits = m[1].replace(/\D/g, '');
    return digits.length >= 3 ? `Bank statement ••${digits.slice(-4)}` : 'Bank statement';
  }
  if (/^(bank )?statements?$/.test(lower) || /^bank statements?( \d{1,2})?$/.test(lower)) return 'Bank statement';
  return t.replace(/\bw ?2\b/i, 'W-2').replace(/\bpay ?stub\b/i, 'Pay stub');
}

/** A stored file name → the name a borrower reads. Pure: no I/O, no document type. */
export function friendlyFileName(raw) {
  let s = String(raw == null ? '' : raw).trim();
  if (/\s·\s/.test(s)) return s;                          // already a borrower name (idempotent)
  s = s.replace(/\.[a-z0-9]{2,5}$/i, '');                 // the extension
  s = s.replace(/\s*\(\d{1,3}\)\s*$/, '');                // a browser's duplicate suffix "(1)"
  s = s.replace(/[_]+|-+/g, ' ').replace(/\s+/g, ' ').trim();
  s = s.replace(/\s*\(\d{1,3}\)\s*$/, '');                // …also after the separators went
  // 🚨 Never "Step5 …": the step prefix and the section word after it are internal.
  const stepped = /^step ?\d+(\s|$)/i.test(s);
  s = s.replace(/^step ?\d+\s*/i, '');
  if (stepped) s = s.replace(SECTION_WORDS, '');
  const words = s.split(' ').filter(Boolean);
  const date = trailingDate(words);
  const rest = date ? date.rest : words;
  let title = titleFor(rest, !!date);
  if (!title) title = date ? 'Document' : '';
  title = title ? title.charAt(0).toUpperCase() + title.slice(1) : '';
  if (!title && !date) return 'Document';
  return date ? `${title} · ${date.label}` : title;
}

/**
 * The borrower's name for a document row: Doorly's own documents by type; everything else from its stored name.
 * d: { document_type, name | original_name | filename }
 */
export function borrowerDocumentName(d) {
  const doc = d || {};
  const t = String(doc.document_type || '');
  if (DOORLY_SIGNED_NAMES[t]) return DOORLY_SIGNED_NAMES[t];
  if (DOORLY_SENT_NAMES[t]) return DOORLY_SENT_NAMES[t];
  const stored = doc.name || doc.original_name || doc.filename;
  if (stored) return friendlyFileName(stored);
  return t ? friendlyFileName(t.replace(/__\w+$/, '')) : 'Document';
}
